import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Background } from "../components/Background";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { ShieldLogo } from "../components/ui/Logo";
import { useSocket } from "../state/SocketContext";

export default function Results() {
  const navigate = useNavigate();
  const { matchOver, socketId, resetMatchArtifacts, leaveRoom } = useSocket();

  // If user lands here without a result (refresh, etc.) bounce home.
  useEffect(() => {
    if (!matchOver) {
      const t = window.setTimeout(() => navigate("/"), 600);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [matchOver, navigate]);

  const rows = useMemo(() => {
    if (!matchOver) return [];
    return Object.entries(matchOver.scores)
      .map(([id, s]) => ({
        id,
        name: s.name,
        survivedMs: s.survivedMs,
        alive: s.alive,
        self: id === socketId,
        winner: id === matchOver.winnerId,
      }))
      .sort((a, b) => b.survivedMs - a.survivedMs);
  }, [matchOver, socketId]);

  if (!matchOver) {
    return (
      <Background>
        <div className="flex min-h-screen items-center justify-center">
          <p className="text-sm uppercase tracking-widest text-white/60">No active match…</p>
        </div>
      </Background>
    );
  }

  const youWon = matchOver.winnerId === socketId;
  const headline = matchOver.tie
    ? "Stalemate"
    : youWon
    ? "Victory"
    : "Defeated";
  const subtitle = matchOver.tie
    ? "Both heroes fell at the same instant."
    : matchOver.reason === "OPPONENT_LEFT"
    ? "Your opponent abandoned the mission."
    : matchOver.reason === "TIME_LIMIT"
    ? "Time ran out — longest run wins."
    : "Last hero standing.";

  const rematch = async () => {
    resetMatchArtifacts();
    navigate("/lobby");
  };

  const home = async () => {
    await leaveRoom();
    navigate("/");
  };

  return (
    <Background>
      <div className="mx-auto flex min-h-screen max-w-3xl flex-col items-center justify-center px-4 py-10">
        <header className="mb-6 flex flex-col items-center gap-2 text-center">
          <ShieldLogo size={80} />
          <h1
            className={[
              "font-display text-6xl uppercase tracking-[0.3em]",
              matchOver.tie ? "text-cap-gold" : youWon ? "text-emerald-400" : "text-cap-red",
            ].join(" ")}
          >
            {headline}
          </h1>
          <p className="text-sm uppercase tracking-widest text-white/60">{subtitle}</p>
        </header>

        <Card glow className="w-full">
          <table className="w-full">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-white/40">
                <th className="pb-3">#</th>
                <th className="pb-3">Hero</th>
                <th className="pb-3">Survived</th>
                <th className="pb-3 text-right">Score</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr
                  key={r.id}
                  className={[
                    "border-t border-white/10",
                    r.winner ? "bg-cap-gold/10" : "",
                  ].join(" ")}
                >
                  <td className="py-3 font-display text-xl text-white/80">{i + 1}</td>
                  <td className="py-3">
                    <span className="font-display text-lg text-white">{r.name}</span>
                    {r.self && (
                      <span className="ml-2 rounded-full bg-white/10 px-2 py-0.5 text-[10px] uppercase tracking-widest text-white/70">
                        you
                      </span>
                    )}
                    {r.winner && (
                      <span className="ml-2 rounded-full bg-cap-gold/20 px-2 py-0.5 text-[10px] uppercase tracking-widest text-cap-gold">
                        winner
                      </span>
                    )}
                  </td>
                  <td className="py-3 text-white/80">{(r.survivedMs / 1000).toFixed(1)}s</td>
                  <td className="py-3 text-right font-display text-2xl text-cap-gold">
                    {Math.floor((r.survivedMs / 1000) * 10).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
            <Button variant="primary" size="lg" onClick={rematch}>
              Rematch
            </Button>
            <Button variant="ghost" size="lg" onClick={home}>
              Home
            </Button>
          </div>
        </Card>
      </div>
    </Background>
  );
}
